import { supabase } from '../lib/supabase'
import { treatmentOf, gstComponent } from '../lib/gstTreatment'
import { Deal } from '../types'

/**
 * P&L — Winnie, v4 of the change register.
 *
 *   "New Report call P&L build like the below accounts in the platform should
 *    transfer to Xero."
 *
 * The split she asked for is the important part, and it is not obvious: a sale
 * is classified by how the vehicle was ACQUIRED, not by how it was sold.
 *
 *   Vehicle Sales – Full GST Supply  — bought from a registered company, or an
 *                                      LMCT / auction / tender seller, on a tax
 *                                      invoice with GST paid.
 *   Vehicle Sales – Margin Scheme    — bought from a private individual, or a
 *                                      company not registered for GST, with no
 *                                      GST in the price.
 *
 * That is exactly the `gst_treatment` the platform already carries per deal, so
 * the report reads it rather than guessing from the sale side.
 *
 * On GST. A P&L goes to Xero exclusive of GST, and for three of these lines the
 * exclusive figure is arithmetic: a Full GST Supply sale and a GST-vendor
 * purchase each carry one eleventh, and a private-vendor purchase carries none.
 * A margin-scheme SALE is the exception — the GST falls on the margin, and which
 * period that credit lands in is the realised-vs-projected question still open
 * with Thomas Sparks. Rather than pick an answer that would move a BAS figure,
 * those rows report gross and leave the ex-GST column empty with a reason. The
 * screen says so; it does not quietly assume.
 */

/*
 * A figure is only useful if you can see what it is made of. Winnie asked for
 * the P&L to drill down to itemised transactions; these are the rows behind an
 * account line, carried on the line itself rather than re-queried when someone
 * opens it, so what appears underneath a number is by construction the same set
 * that produced the number.
 */
export interface PlRow {
  id: string
  when: string
  what: string
  reference: string
  gross: number
}

export interface PlLine {
  account: string
  gross: number
  exGst: number | null
  vehicles?: number
rows?: PlRow[]
  note?: string
}

export interface ProfitAndLoss {
  periodLabel: string
  tradingIncome: PlLine[]
  totalIncomeGross: number
  costOfSales: PlLine[]
  totalCostGross: number
  grossProfitGross: number
}

/** Categories the platform records, mapped to the accounts Winnie keeps in Xero. */
// `expenses.category` stores a snake_case key behind a check constraint. The
// pretty names are display only, and matching on them — which the first cut of
// this report did — silently returns nothing: every Cost of Sales line reads $0
// and looks like an empty month rather than a broken mapping.
const COS_ACCOUNTS: { account: string; categories: string[] }[] = [
  { account: 'COS – Freight and transportation costs', categories: ['transport'] },
  { account: 'COS – Inspection Costs', categories: ['inspection'] },
  { account: 'COS – PPSR Searches & Registrations', categories: ['ppsr'] },
  { account: 'COS – Reconditioning (minor repairs & services)', categories: ['reconditioning'] },
  { account: 'COS – Sourcing & Buying Fee', categories: ['buying_fee'] },
  // Winnie's chart of accounts has this line; the platform has no expense
  // category that feeds it yet. Showing it at zero is honest — dropping it would
  // hide the gap, and folding 'Other' into it would misstate the account.
  { account: 'COS – Statutory Charges – Rego & Safety', categories: [] },
]
/**
 * The Cost of Sales account an expense category posts to, in Winnie's own chart.
 *
 * Exported because the accrual reversal journal credits the same account the original
 * estimate was charged to. That entry has to name a real account, and inventing one here when
 * the list already sits a few lines above is how two parts of a platform end up disagreeing
 * about what a cost is called.
 */
export function cosAccountFor(category: string | null | undefined): string {
  const key = String(category || '')
    .toLowerCase()
    .trim()
  const hit = COS_ACCOUNTS.find((a) => a.categories.includes(key))
  return hit ? hit.account : 'COS \u2013 Other (uncategorised)'
}

const round2 = (n: number) => Math.round(n * 100) / 100
const num = (v: unknown) => Number(v || 0)

const acquiredOn = (d: Deal) => d.acquired_date || d.paid_at || d.created_at || null
const soldOn = (d: Deal) => d.sale_date || d.sold_at || null

export async function loadProfitAndLoss(
  from: string,
  to: string,
  periodLabel: string
): Promise<ProfitAndLoss> {
  const start = new Date(from + 'T00:00:00Z')
  const end = new Date(to + 'T00:00:00Z')
  end.setUTCDate(end.getUTCDate() + 1) // `to` is inclusive

  const within = (iso: string | null) => {
    if (!iso) return false
    const t = new Date(iso).getTime()
    return t >= start.getTime() && t < end.getTime()
  }

  const [dealRes, expRes] = await Promise.all([
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('expenses').select('*'),
  ])
  if (dealRes.error) throw dealRes.error
  if (expRes.error) throw expRes.error

  const deals = (dealRes.data || []) as Deal[]
  const expenses = (expRes.data || []) as Array<Record<string, any>>

  const sold = deals.filter((d) => within(soldOn(d)) && num(d.sale_price) > 0)
  const bought = deals.filter((d) => within(acquiredOn(d)) && num(d.valuation_amount) > 0)

  const fullGstSold = sold.filter((d) => treatmentOf(d) === 'tax_invoice')
  const marginSold = sold.filter((d) => treatmentOf(d) !== 'tax_invoice')

  const vehicleLabel = (d: Deal) =>
    [d.vehicle_year, d.vehicle_make, d.vehicle_model].filter(Boolean).join(' ') ||
    d.seller_name ||
    'Vehicle'
  const vehicleRef = (d: Deal) => String(d.vehicle_vin || d.autograb_vin || '')
  const saleRow = (d: Deal): PlRow => ({
    id: String(d.id),
    when: String(soldOn(d) || '').slice(0, 10),
    what: vehicleLabel(d),
    reference: vehicleRef(d),
    gross: round2(num(d.sale_price)),
  })
  const buyRow = (d: Deal): PlRow => ({
    id: String(d.id),
    when: String(acquiredOn(d) || '').slice(0, 10),
    what: vehicleLabel(d),
    reference: vehicleRef(d),
    gross: round2(num(d.valuation_amount)),
  })
  const expenseRow = (e: Record<string, any>): PlRow => ({
    id: String(e.id),
    when: String(e.incurred_on || '').slice(0, 10),
    what: String(e.description || e.category || 'Expense'),
    reference: String(e.supplier || e.invoice_number || ''),
    gross: round2(num(e.amount)),
  })

  const sum = (rows: Deal[], pick: (d: Deal) => number) =>
    round2(rows.reduce((s, d) => s + pick(d), 0))

  const fullGstGross = sum(fullGstSold, (d) => num(d.sale_price))
  const marginGross = sum(marginSold, (d) => num(d.sale_price))

  const tradingIncome: PlLine[] = [
    {
      account: 'Vehicle Sales – Full GST Supply',
      gross: fullGstGross,
      exGst: round2(fullGstGross - gstComponent(fullGstGross)),
      vehicles: fullGstSold.length,
      rows: fullGstSold.map(saleRow),
    },
    {
      account: 'Vehicle Sales – Margin Scheme',
      gross: marginGross,
        /*
           Winnie, 1 Sep: "In P&L Vehicle Sales - Margin Scheme - Ex GST column:
           should be Gross amount divided by 1.1 instead of zero as current."

           Presented as she asked. Worth knowing what it is and is not: under
           Division 66 the GST actually payable on a margin-scheme sale is one
           eleventh of the MARGIN, not of the sale, so gross / 1.1 takes out more
           GST than is owed. It is a presentation basis for the P&L, not the BAS
           figure - the BAS number is the one on the GST register.
        */
        exGst: round2(marginGross / 1.1),
        vehicles: marginSold.length,
        rows: marginSold.map(saleRow),
        note:
          'Ex GST shown at gross divided by 1.1. GST actually falls on the margin, so the BAS figure is the one on the Margin Scheme (GST Register).',
    },
  ]

  /*
   * Closing and opening stock - the missing half of cost of sales.
   *
   * Beta test, 6 Sep: the P&L reported FY2025/26 at a $149,078.58 LOSS. It was
   * comparing the 142 cars SOLD in the year against the 154 cars BOUGHT in it.
   * Those are different sets of cars, so the gross profit meant nothing: the 12
   * still in stock at 30 June were charged in full against a year in which they
   * earned nothing.
   *
   * Cost of sales is opening stock + purchases - closing stock. Purchases alone
   * is a purchase ledger, not a cost of sales.
   *
   * Stock is valued here at buy-in (valuation_amount), which is the same basis
   * the purchase lines above use. That matters: mixing a landed-cost stock
   * figure into a gross-purchase ledger would not cancel out. The Month-End tab
   * values the same cars at landed cost for the Xero journal, so the two
   * screens will differ by the GST component by design.
   *
   * THOMAS SPARKS SHOULD CONFIRM THE VALUATION BASIS before this feeds a
   * lodgement. What is not in question is that the old figure was wrong.
   */
  const heldAt = (d: Deal, boundaryMs: number) => {
    const acquired = acquiredOn(d)
    if (!acquired) return false
    if (new Date(acquired).getTime() > boundaryMs) return false
    const sold = soldOn(d)
    if (!sold) return true
    return new Date(sold).getTime() > boundaryMs
  }

  // start is the first instant of `from`, so a car sold on the first day of the
  // period was still ours the moment the period opened.
  // A car only counts as stock if we actually paid something for it. Without
  // this the count picked up a deal still at sourcing that we do not own -
  // it added $0 to the value but made the line read 13 vehicles where the
  // Month-End stocktake says 12, which is exactly the kind of small
  // disagreement between two screens that costs an afternoon to chase.
  const owned = (d: Deal) => num(d.valuation_amount) > 0
  const openingHeld = deals.filter((d) => owned(d) && heldAt(d, start.getTime() - 1))
  const closingHeld = deals.filter((d) => owned(d) && heldAt(d, end.getTime() - 1))

  const openingStockGross = sum(openingHeld, (d) => num(d.valuation_amount))
  const closingStockGross = sum(closingHeld, (d) => num(d.valuation_amount))

  const gstVendorGross = sum(bought.filter((d) => treatmentOf(d) === 'tax_invoice'), (d) => num(d.valuation_amount))
  const privateVendorGross = sum(bought.filter((d) => treatmentOf(d) !== 'tax_invoice'), (d) => num(d.valuation_amount))

  const inPeriodExpenses = expenses.filter((e) => within(e.incurred_on))

  const costOfSales: PlLine[] = COS_ACCOUNTS.map(({ account, categories }) => {
    const rows = inPeriodExpenses.filter((e) => categories.includes(String(e.category)))
    const gross = round2(rows.reduce((s, e) => s + num(e.amount), 0))
    const gst = round2(rows.reduce((s, e) => s + num(e.gst_amount), 0))
    return {
      account,
      gross,
      exGst: round2(gross - gst),
      rows: rows.map(expenseRow),
      note: categories.length === 0 ? 'No expense category feeds this account yet.' : undefined,
    }
  })

  const otherGross = round2(
    inPeriodExpenses
      .filter((e) => !COS_ACCOUNTS.some((a) => a.categories.includes(String(e.category))))
      .reduce((s, e) => s + num(e.amount), 0)
  )
  const otherGst = round2(
    inPeriodExpenses
      .filter((e) => !COS_ACCOUNTS.some((a) => a.categories.includes(String(e.category))))
      .reduce((s, e) => s + num(e.gst_amount), 0)
  )
  if (otherGross !== 0) {
    costOfSales.push({
      account: 'COS – Other (uncategorised)',
      gross: otherGross,
      exGst: round2(otherGross - otherGst),
      note: 'Expenses whose category does not map to one of the accounts above.',
    })
  }

  costOfSales.push(
    {
      account: 'Vehicle Purchases – GST Vendor',
      gross: gstVendorGross,
      exGst: round2(gstVendorGross - gstComponent(gstVendorGross)),
      vehicles: bought.filter((d) => treatmentOf(d) === 'tax_invoice').length,
      rows: bought.filter((d) => treatmentOf(d) === 'tax_invoice').map(buyRow),
    },
    {
      account: 'Vehicle Purchases – Private Vendor',
      gross: privateVendorGross,
      exGst: privateVendorGross,
      vehicles: bought.filter((d) => treatmentOf(d) !== 'tax_invoice').length,
      rows: bought.filter((d) => treatmentOf(d) !== 'tax_invoice').map(buyRow),
      note: 'No GST in the price, so gross and ex-GST are the same figure.',
    }
  )

  if (openingStockGross > 0) {
    costOfSales.push({
      account: 'Opening Stock - vehicles on hand at start',
      gross: openingStockGross,
      exGst: null,
      vehicles: openingHeld.length,
      rows: openingHeld.map(buyRow),
      note: 'Carried in from the previous period at buy-in.',
    })
  }

  // Negative on purpose: closing stock is a credit against cost of sales, and
  // showing it as its own line keeps the arithmetic auditable rather than
  // netting it away inside the purchase figures.
  if (closingStockGross > 0) {
    costOfSales.push({
      account: 'Less Closing Stock - vehicles still on hand at end',
      gross: -closingStockGross,
      exGst: null,
      vehicles: closingHeld.length,
      rows: closingHeld.map((d) => ({ ...buyRow(d), gross: -round2(num(d.valuation_amount)) })),
      note: 'Cars bought but not yet sold. Their cost belongs to the period they sell in, not this one.',
    })
  }

  const totalIncomeGross = round2(tradingIncome.reduce((s, l) => s + l.gross, 0))
  const totalCostGross = round2(costOfSales.reduce((s, l) => s + l.gross, 0))

  return {
    periodLabel,
    tradingIncome,
    totalIncomeGross,
    costOfSales,
    totalCostGross,
    grossProfitGross: round2(totalIncomeGross - totalCostGross),
  }
}
